import React, { FC, useState } from 'react'
import { LatestContractDtoFragment, EmployeeTagInfo, PersonalProfileFragment, PublicProfileFragment, RoleName } from '../../../types'
import { Col } from 'antd'
import { useDateTime } from '../../../shared/useDateTime'
import { useTranslation } from 'react-i18next'
import { ProfileLabelListComponent } from '../ProfileLabelListComponent'
import { employeeInformationComponentUiProps } from './employeeInformationComponentUiProps'
import { rankingUiProps } from '../../cultivatingTalents/ranking.uiprops'
import { RankingTagHistoryModal } from '../../cultivatingTalents/RankingTagHistoryModal'
import PerText from '../../../components/custom_components/Text'
import PerSpacer from '../../../components/custom_components/Spacer'
import PerFlexRow from '../../../components/custom_components/FlexRow'
import PerButtonCircle from '../../../components/custom_components/ButtonCircle'
import { PerTag } from '../../../components/custom_components/Tag'
import { MANAGER_ROLES, PARTNER_LEVELS } from '../../../shared/permissions/levelAndRoleGroups'
import { TimeZoneAndLocale } from '../../../types.custom'
import { useAuth } from '../../../context/auth-context'

interface EmployeeInformationComponentProps {
  profile: PersonalProfileFragment | PublicProfileFragment
  publicProfile: boolean
  currentContract?: LatestContractDtoFragment
  tagHistory?: EmployeeTagInfo[]
  timezone: TimeZoneAndLocale
}

export const EmployeeInformationComponent: FC<EmployeeInformationComponentProps> = ({
  profile,
  publicProfile,
  currentContract,
  tagHistory,
  timezone,
}) => {
  const { user } = useAuth()
  const { formatDateShort, formatDateDayMonth } = useDateTime(timezone)
  const [modalOpenTagHistory, setModalOpenTagHistory] = useState(false)

  const { t } = useTranslation()
  const { tagColor } = rankingUiProps()
  const {
    isPersonalProfile,
    showCitizenshipData,
    showChosenFirstName,
    showChosenLastName,
    showAcademicTitle,
    showPrivateEmailAddress,
  } = employeeInformationComponentUiProps(profile)
  const currentEmployeelevel = currentContract && !!currentContract.employeeLevel ? currentContract.employeeLevel.level : undefined
  const hasTagHistory = tagHistory !== undefined && tagHistory.length > 0
  const currentTags = !!tagHistory ? tagHistory.filter((tag) => !tag.removedDate) : []

  const isRankingTagVisible =
    user.roles &&
    user.roles.find((role) => {
      return (
        (role === RoleName.Hr || MANAGER_ROLES.includes(role)) && (!currentEmployeelevel || !PARTNER_LEVELS.includes(currentEmployeelevel))
      )
    })

  const renderMandatory = () => (
    <>
      <ProfileLabelListComponent label={t('profile.personalData.legalFirstName')} value={profile.legalFirstName} />
      <ProfileLabelListComponent label={t('profile.personalData.legalLastName')} value={profile.legalLastName} />
      {profile.dateOfBirth && (
        <ProfileLabelListComponent
          label={t('profile.personalData.dateOfBirth')}
          value={publicProfile ? formatDateDayMonth(profile.dateOfBirth, true) : formatDateShort(profile.dateOfBirth, true)}
        />
      )}
      {showCitizenshipData && profile.__typename === 'PersonalProfile' && profile.citizenships && profile.citizenships.length !== 0 && (
        <ProfileLabelListComponent
          data-testid="profile-citizenships"
          label={t('profile.personalData.citizenships')}
          value={[...profile.citizenships]
            .sort((a, b) => a.displayOrder - b.displayOrder)
            .reduce(
              (acc, c) => (!!acc ? acc + ', ' + t(`global.citizenship.${c.country}`) : acc + ' ' + t(`global.citizenship.${c.country}`)),
              '',
            )}
        />
      )}
      <ProfileLabelListComponent label={t('profile.personalData.replyEntryDate')} value={formatDateShort(profile.replyEntryDate, true)} />
    </>
  )

  const renderOther = () => (
    <>
      {showChosenFirstName && profile.chosenFirstName && (
        <ProfileLabelListComponent label={t('profile.personalData.chosenFirstName')} value={profile.chosenFirstName} />
      )}
      {showChosenLastName && profile.chosenLastName && (
        <ProfileLabelListComponent label={t('profile.personalData.chosenLastName')} value={profile.chosenLastName} />
      )}
      {showAcademicTitle && profile.academicTitle && (
        <ProfileLabelListComponent label={t('profile.personalData.academicTitle')} value={profile.academicTitle} />
      )}
      {showPrivateEmailAddress && profile.__typename === 'PersonalProfile' && profile.privateEmailAddress && (
        <ProfileLabelListComponent label={t('profile.personalData.privateEmailAddress')} value={profile.privateEmailAddress} />
      )}
    </>
  )

  const renderTalentTag = () => (
    <PerSpacer mt="l">
      <ProfileLabelListComponent
        label={t('profile.contract.rankingTag')}
        value={
          <PerFlexRow align="middle">
            {currentTags.length > 0 ? (
              currentTags.map((tag, index) => {
                return (
                  <React.Fragment key={index}>
                    <PerTag color={tagColor(tag.type)}>{t(`global.employeeTags.general.${tag.type}`)}</PerTag>
                  </React.Fragment>
                )
              })
            ) : (
              <PerTag>{t(`global.employeeTags.general.untagged`)}</PerTag>
            )}
            {hasTagHistory && (
              <PerButtonCircle
                onClick={() => setModalOpenTagHistory(!modalOpenTagHistory)}
                icon="view_fill"
                color="primary"
                variant="pastel"
                size="small"
                ml="xxs"
                my="xxs"
              />
            )}
          </PerFlexRow>
        }
      />
    </PerSpacer>
  )

  return (
    <>
      <PerText appearance="tile-heading" mb="xl">
        {t('profile.personalData.header')}
      </PerText>

      {isPersonalProfile && (
        <PerFlexRow>
          <Col span={12}>{renderMandatory()}</Col>
          <Col span={12}>{renderOther()}</Col>
        </PerFlexRow>
      )}

      {publicProfile && (
        <>
          {tagHistory && tagHistory.length > 0 && (
            <RankingTagHistoryModal
              isOpen={modalOpenTagHistory}
              onCancel={() => setModalOpenTagHistory(!modalOpenTagHistory)}
              modalData={tagHistory}
            />
          )}
          {renderMandatory()}
          {renderOther()}
          {isRankingTagVisible && renderTalentTag()}
        </>
      )}
    </>
  )
}
